/**
 * NodeShell -- Common wrapper for all custom React Flow nodes.
 *
 * Provides:
 * - Left colored border (4px, color from NODE_TYPE_CONFIG)
 * - Icon resolved from lucide-react by name
 * - Node label (bold) + optional subtitle (config preview)
 * - Status indicator dot (idle/running/success/error)
 * - React Flow Handle components for source/target connections
 * - Selected state: primary ring
 * - Compact design (~200px wide, ~70px tall)
 * - Dark mode support
 */

import { type ReactNode } from 'react';
import { Handle, Position } from '@xyflow/react';
import {
  Zap, Plug, GitBranch, GitMerge, ShieldCheck, Repeat, Clock, Bell, Globe, Sparkles,
  Bot, Search, Variable, Code, Workflow, Route, Filter, Sigma, UnfoldVertical,
  ArrowUpDown, ListEnd, CopyMinus, ShieldAlert, Octagon, Hourglass, Reply,
  CalendarClock, FileText, Braces, Send,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { NODE_TYPE_CONFIG, type NodeType } from '@/lib/mock-automate-data';
import { NodeInspectBadge, type NodeInspectData } from './node-inspect-badge';

// ── Icon map (lucide name -> component) ──────────────────────────────────

const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  zap: Zap, bot: Bot, plug: Plug, 'git-branch': GitBranch, 'git-merge': GitMerge,
  sparkles: Sparkles, 'shield-check': ShieldCheck, repeat: Repeat, clock: Clock,
  globe: Globe, bell: Bell, search: Search, variable: Variable, code: Code,
  workflow: Workflow, route: Route, filter: Filter, sigma: Sigma,
  'unfold-vertical': UnfoldVertical, 'arrow-up-down': ArrowUpDown,
  'list-end': ListEnd, 'copy-minus': CopyMinus, 'shield-alert': ShieldAlert,
  octagon: Octagon, hourglass: Hourglass, reply: Reply,
  'calendar-clock': CalendarClock, 'file-text': FileText, braces: Braces, send: Send,
};

// ── Handle position helpers ──────────────────────────────────────────────

function toReactFlowPosition(pos: 'top' | 'bottom' | 'left' | 'right'): Position {
  const map: Record<string, Position> = {
    top: Position.Top,
    bottom: Position.Bottom,
    left: Position.Left,
    right: Position.Right,
  };
  return map[pos];
}

// ── Status dot styles ────────────────────────────────────────────────────

const STATUS_STYLES: Record<string, string> = {
  idle: 'bg-zinc-400 dark:bg-zinc-500',
  running: 'bg-blue-500 animate-pulse',
  success: 'bg-emerald-500',
  error: 'bg-red-500',
};

// ── Props ────────────────────────────────────────────────────────────────

export interface SourceHandle {
  id: string;
  position: 'bottom' | 'right';
  label?: string;
}

export interface TargetHandle {
  id: string;
  position: 'top' | 'left';
}

export interface NodeShellProps {
  type: NodeType;
  label: string;
  subtitle?: string;
  status?: 'idle' | 'running' | 'success' | 'error';
  selected?: boolean;
  sourceHandles?: SourceHandle[];
  targetHandles?: TargetHandle[];
  children?: ReactNode;
  /** Inspect data shown as a clickable badge after test run */
  inspectData?: NodeInspectData;
  /** Stagger index for badge pop-in animation */
  inspectStaggerIndex?: number;
  /** Whether this node is the current step in step-through mode */
  isCurrentStep?: boolean;
  /** Whether this node is the next step in step-through mode */
  isNextStep?: boolean;
}

// ── Border color map (Tailwind classes from NODE_TYPE_CONFIG.borderColor) ─

const BORDER_COLOR_MAP: Record<string, string> = {
  'border-l-amber-500': 'border-l-amber-500',
  'border-l-purple-500': 'border-l-purple-500',
  'border-l-blue-500': 'border-l-blue-500',
  'border-l-emerald-500': 'border-l-emerald-500',
  'border-l-violet-500': 'border-l-violet-500',
  'border-l-red-500': 'border-l-red-500',
  'border-l-indigo-500': 'border-l-indigo-500',
  'border-l-slate-500': 'border-l-slate-500',
  'border-l-sky-500': 'border-l-sky-500',
  'border-l-orange-500': 'border-l-orange-500',
};

// ── Component ────────────────────────────────────────────────────────────

export function NodeShell({
  type,
  label,
  subtitle,
  status = 'idle',
  selected = false,
  sourceHandles = [],
  targetHandles = [],
  children,
  inspectData,
  inspectStaggerIndex = 0,
  isCurrentStep = false,
  isNextStep = false,
}: NodeShellProps) {
  const config = NODE_TYPE_CONFIG[type];
  const Icon = ICON_MAP[config.icon] ?? Zap;
  const borderClass = BORDER_COLOR_MAP[config.borderColor] ?? 'border-l-zinc-500';

  // Calculate vertical distribution for right-side handles
  const rightHandles = sourceHandles.filter(h => h.position === 'right');
  const bottomHandles = sourceHandles.filter(h => h.position === 'bottom');

  return (
    <div
      data-testid={`canvas-node-${type}`}
      className={cn(
        'relative min-w-[200px] max-w-[220px] rounded-lg border-l-4 bg-background shadow-sm border border-border',
        borderClass,
        selected && 'ring-2 ring-primary ring-offset-1 ring-offset-background',
        isCurrentStep && 'ring-2 ring-blue-500 ring-offset-2 ring-offset-background',
        isNextStep && 'border-dashed border-2 border-blue-400/60',
      )}
      style={isCurrentStep ? { animation: 'pulse-ring 1.5s ease-in-out infinite' } : undefined}
    >
      {/* Inspect badge (after test run) */}
      {inspectData && (
        <NodeInspectBadge data={inspectData} staggerIndex={inspectStaggerIndex} />
      )}
      {/* Target handles */}
      {targetHandles.map((h) => (
        <Handle
          key={h.id}
          id={h.id}
          type="target"
          position={toReactFlowPosition(h.position)}
          className="!w-2.5 !h-2.5 !bg-zinc-400 dark:!bg-zinc-500 !border-2 !border-background"
        />
      ))}

      {/* Body */}
      <div className="flex items-center gap-2.5 px-3 py-2.5">
        {/* Icon */}
        <div
          className={cn(
            'flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-md',
            'bg-muted/60',
          )}
        >
          <Icon className={cn('h-4 w-4', config.color)} />
        </div>

        {/* Text */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-xs font-semibold leading-tight truncate">{label}</span>
            <span className={cn('flex-shrink-0 w-1.5 h-1.5 rounded-full', STATUS_STYLES[status])} />
          </div>
          {subtitle && (
            <p className="text-[10px] text-muted-foreground leading-tight mt-0.5 truncate">{subtitle}</p>
          )}
        </div>
      </div>

      {/* Additional content */}
      {children}

      {/* Bottom source handles */}
      {bottomHandles.map((h, i) => {
        const offset = bottomHandles.length > 1
          ? ((i + 1) / (bottomHandles.length + 1)) * 100
          : 50;
        return (
          <Handle
            key={h.id}
            id={h.id}
            type="source"
            position={Position.Bottom}
            style={{ left: `${offset}%` }}
            className="!w-2.5 !h-2.5 !bg-zinc-400 dark:!bg-zinc-500 !border-2 !border-background"
          >
            {h.label && (
              <span className="absolute top-3 left-1/2 -translate-x-1/2 text-[8px] text-muted-foreground whitespace-nowrap pointer-events-none">
                {h.label}
              </span>
            )}
          </Handle>
        );
      })}

      {/* Right source handles */}
      {rightHandles.map((h, i) => {
        const offset = rightHandles.length > 1
          ? ((i + 1) / (rightHandles.length + 1)) * 100
          : 50;
        return (
          <Handle
            key={h.id}
            id={h.id}
            type="source"
            position={Position.Right}
            style={{ top: `${offset}%` }}
            className="!w-2.5 !h-2.5 !bg-zinc-400 dark:!bg-zinc-500 !border-2 !border-background"
          >
            {h.label && (
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[8px] text-muted-foreground whitespace-nowrap pointer-events-none">
                {h.label}
              </span>
            )}
          </Handle>
        );
      })}
    </div>
  );
}
